import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Box,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ChevronRight,
  Warning,
  Info,
  CheckCircle,
  Error,
  AccessTime,
  Login,
  Logout,
} from "@mui/icons-material";
import { memo, useMemo } from "react";

// Helper function to get relative time
const getRelativeTime = (timestamp) => {
  if (!timestamp) return "";

  const now = new Date();
  const then = new Date(timestamp);
  const diffInSeconds = Math.floor((now - then) / 1000);

  if (diffInSeconds < 60) return "Just now";
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return `${mins}m ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h ago`;
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago`;
  }
  // For older dates, show the date
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const RecentItem = ({
  timestamp,
  title,
  subtitle,
  severity,
  deviceId,
  onItemClick,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const severityConfig = useMemo(() => {
    const configs = {
      critical: {
        color: theme.palette.error.main,
        icon: <Error fontSize="small" />,
        label: "Critical",
      },
      high: {
        color: theme.palette.warning.main,
        icon: <Warning fontSize="small" />,
        label: "High",
      },
      medium: {
        color: theme.palette.info.main,
        icon: <Info fontSize="small" />,
        label: "Medium",
      },
      low: {
        color: theme.palette.success.main,
        icon: <CheckCircle fontSize="small" />,
        label: "Low",
      },
      enter: {
        color: theme.palette.success.main,
        icon: <Login fontSize="small" />,
        label: "Enter",
      },
      exit: {
        color: theme.palette.warning.main,
        icon: <Logout fontSize="small" />,
        label: "Exit",
      },
      info: {
        color: theme.palette.info.main,
        icon: <Info fontSize="small" />,
        label: "Info",
      },
    };
    return (
      configs[severity?.toLowerCase()] || {
        color: theme.palette.text.secondary,
        icon: <Info fontSize="small" />,
        label: severity || "Alert",
      }
    );
  }, [severity, theme]);

  const relativeTime = useMemo(() => getRelativeTime(timestamp), [timestamp]);

  const handleClick = () => {
    if (deviceId && onItemClick) {
      onItemClick(deviceId);
    } else if (deviceId) {
      navigate(`/device/${deviceId}`);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        cursor: deviceId ? "pointer" : "default",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        borderLeft: `4px solid ${severityConfig.color}`,
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.6)
            : theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.2)"
            : "0 2px 8px rgba(0,0,0,0.04)",
        "&:hover": deviceId
          ? {
              backgroundColor: alpha(severityConfig.color, 0.06),
              borderColor: alpha(severityConfig.color, 0.3),
              transform: "translateX(4px)",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 4px 12px rgba(0,0,0,0.3)"
                  : "0 4px 12px rgba(0,0,0,0.08)",
              "& .chevron-icon": {
                opacity: 1,
                transform: "translateX(0)",
              },
            }
          : {},
      }}
      onClick={handleClick}
    >
      <CardContent sx={{ p: "14px 16px !important" }}>
        <Box display="flex" alignItems="center" gap={2}>
          {/* Severity Icon */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: alpha(severityConfig.color, 0.12),
              color: severityConfig.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {severityConfig.icon}
          </Box>

          {/* Content */}
          <Box flex={1} minWidth={0}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: theme.palette.text.primary,
                lineHeight: 1.5,
              }}
            >
              {title}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.4} mt={0.25}>
              <AccessTime
                sx={{ fontSize: 11, color: theme.palette.text.disabled }}
              />
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.disabled, lineHeight: 1.2 }}
              >
                {relativeTime}
              </Typography>
            </Box>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  mt: 0.25,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Right side - badge + chevron */}
          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
            <Typography
              variant="caption"
              sx={{
                color: severityConfig.color,
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: "0.65rem",
                letterSpacing: "0.5px",
              }}
            >
              {severityConfig.label}
            </Typography>
            {deviceId && (
              <ChevronRight
                className="chevron-icon"
                sx={{
                  fontSize: 20,
                  color: theme.palette.text.secondary,
                  opacity: 0.5,
                  transform: "translateX(-4px)",
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default memo(RecentItem);
